import { z } from "zod";
import { CommunityAsset } from "./asset.js";
import { CommunityAuthor } from "./author.js";
import { CommunityReactionSummary } from "./reaction.js";
// Personal Reset: a member-authored 60-second "video" composed of uploaded
// images, an audio track, text overlays, and per-frame filters. We do not
// render or store an actual video file. The data structure persisted here is
// replayed by a custom Skia-based player on the mobile client, which draws
// each frame for its computed duration with crossfades, ken-burns, and the
// selected filter, while the audio track plays in parallel.
//
// Total runtime is always 60 seconds; per-frame duration is 60 / N.
export const PERSONAL_RESET_DURATION_SEC = 60;
export const PersonalResetFilter = z.enum([
    "none",
    "warm",
    "noir",
    "vintage",
    "vivid",
    "mono",
    "dream",
    "bloom",
]);
// Font presets available in the editor. The set is intentionally narrow so
// every option loads eagerly without a font-download step; each id maps to a
// bundled .ttf via the PERSONAL_RESET_FONTS table on the mobile client.
export const PersonalResetFontFamily = z.enum([
    "serif", // PlayfairDisplay 500 - the existing title face
    "serifItalic", // PlayfairDisplay 500 Italic - editorial
    "reading", // Newsreader 400 - readable body serif
    "readingItalic", // Newsreader 400 Italic - quiet pull-quote
    "sans", // Inter Tight 600 - sans display
    "display", // Poppins 700 - bold geometric display
    "hand", // Caveat 600 - handwritten script
    "mono", // Space Mono 400 - typewriter/editor
    "rounded", // Baloo 2 700 - chunky rounded bubble
]);
// Entrance animation the player runs on a text overlay when its frame's
// slot starts. "none" renders statically; the rest play once over ~700ms.
export const PersonalResetTextAnimation = z.enum([
    "none", // static
    "fade", // opacity 0 -> 1
    "rise", // slides up ~24px while fading in
    "pop", // scales 0.6 -> 1 with a small overshoot while fading in
    "spin", // rotates in from -30 degrees while fading in
]);
// A draggable, pinchable, rotatable text object placed on a frame. Multiple
// overlays per frame are supported so the user can layer a title + a small
// caption + a date stamp, like an Instagram post. Position is normalized
// (textX, textY in [0, 1]) so it scales with any canvas size; scale and
// rotation are applied around that anchor.
export const PersonalResetTextOverlay = z.object({
    // Client-generated id so the editor can track overlays through drags and
    // reorders before the server persists them. Server preserves through save.
    id: z.string().min(1),
    // What this overlay renders. "text" draws `text` in the chosen font;
    // "emoji" draws `text` (one emoji) with the platform color-emoji face;
    // "sticker" draws the bundled image named by `stickerId`. All kinds share
    // the same position/scale/rotation/animation fields.
    kind: z.enum(["text", "emoji", "sticker"]).default("text"),
    // Id into the client's curated sticker registry (bundled images only;
    // never a URL). Null unless kind is "sticker".
    stickerId: z.string().max(40).nullable().default(null),
    text: z.string().max(280).default(""),
    textX: z.number().min(0).max(1).default(0.5),
    textY: z.number().min(0).max(1).default(0.5),
    // Multiplier on the font's base size. 1 = base, 2 = double, 0.5 = half.
    scale: z.number().min(0.4).max(4).default(1),
    // Radians, clamped to plus-or-minus 90 degrees.
    rotation: z
        .number()
        .min(-Math.PI / 2)
        .max(Math.PI / 2)
        .default(0),
    fontFamily: PersonalResetFontFamily.default("serif"),
    // Hex color, e.g. `#ffffff`. Preset palette or a custom HSB pick; always
    // normalized to 6/8-digit hex.
    color: z
        .string()
        .regex(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/)
        .default("#ffffff"),
    animation: PersonalResetTextAnimation.default("none"),
    // How many consecutive frames this overlay stays visible for, starting at
    // the frame it belongs to. 1 = just its own frame (the default).
    spanFrames: z.number().int().min(1).max(40).default(1),
});
// One frame in the reset. imageUrl is the asset's gated delivery URL
// (`/api/community/assets/:id`) so the player can fetch with the bearer token
// through AuthGatedImage. assetId is stored for provenance / deletion /
// re-upload.
//
// Text carries as a list of overlays (see PersonalResetTextOverlay) so the
// editor can support tap-to-add, drag, pinch, rotate, font + color per
// overlay - the Instagram / CapCut interaction model. Older saved rows that
// predate overlays simply parse to an empty list thanks to the default.
export const PersonalResetFrame = z.object({
    id: z.string().uuid(),
    assetId: z.string().uuid(),
    imageUrl: z.string().min(1),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    textOverlays: z.array(PersonalResetTextOverlay).default([]),
    filter: PersonalResetFilter.default("none"),
    // How long this frame holds the screen, in seconds. Null means "even
    // share of the default 60-second runtime" (the pre-duration behaviour,
    // which also covers older saved rows).
    durationSec: z.number().min(2).max(60).nullable().default(null),
});
export const PersonalResetAudio = z.object({
    url: z.string().min(1),
    title: z.string().default(""),
    artist: z.string().default(""),
    // Duration of the source audio in seconds, for display only. The player
    // truncates or loops the audio to fit the 60-second runtime.
    durationSec: z.number().positive().default(PERSONAL_RESET_DURATION_SEC),
});
// One placed audio clip on the reset's audio timeline. A reset can layer up
// to 4 tracks; each is a window into a source file from the curated library:
// startSec positions it on the timeline, sourceOffsetSec left-trims into the
// source, durationSec is how long it plays. The legacy single `audio` field
// stays for older rows and mirrors the first track for older clients.
export const PersonalResetAudioTrack = z.object({
    id: z.string().min(1),
    url: z.string().min(1),
    title: z.string().default(""),
    artist: z.string().default(""),
    startSec: z.number().min(0).default(0),
    sourceOffsetSec: z.number().min(0).default(0),
    durationSec: z.number().positive(),
    sourceDurationSec: z.number().positive(),
});
export const PersonalReset = z.object({
    id: z.string().uuid(),
    author: CommunityAuthor,
    title: z.string().min(1).max(140),
    summary: z.string().max(280).default(""),
    posterImageUrl: z.string().nullable(),
    frames: z.array(PersonalResetFrame).min(1).max(40),
    audio: PersonalResetAudio.nullable(),
    audioTracks: z.array(PersonalResetAudioTrack).max(4).default([]),
    durationSec: z.number().positive().default(PERSONAL_RESET_DURATION_SEC),
    // When true, the reset is visible to everyone in the company's community
    // feed. When false, only the author (and direct share recipients) can play
    // it.
    publishedToCommunity: z.boolean().default(false),
    publishedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreatePersonalResetFrameInput = PersonalResetFrame.omit({ id: true }).extend({
    // The mobile client can pass a client-side id so it can correlate its local
    // state with the persisted row. Server rewrites it to a fresh uuid.
    clientId: z.string().optional(),
});
export const CreatePersonalResetRequest = z.object({
    title: z.string().trim().min(1).max(140),
    summary: z.string().trim().max(280).optional(),
    posterAssetId: z.string().uuid().nullable().optional(),
    frames: z.array(CreatePersonalResetFrameInput).min(1).max(40),
    audio: PersonalResetAudio.nullable().optional(),
    audioTracks: z.array(PersonalResetAudioTrack).max(4).optional(),
    publishToCommunity: z.boolean().optional(),
});
export const CreatePersonalResetResponse = z.object({
    reset: PersonalReset,
});
export const UpdatePersonalResetRequest = z.object({
    title: z.string().trim().min(1).max(140).optional(),
    summary: z.string().trim().max(280).optional(),
    posterAssetId: z.string().uuid().nullable().optional(),
    frames: z.array(CreatePersonalResetFrameInput).min(1).max(40).optional(),
    audio: PersonalResetAudio.nullable().optional(),
    audioTracks: z.array(PersonalResetAudioTrack).max(4).optional(),
    publishToCommunity: z.boolean().optional(),
});
export const UpdatePersonalResetResponse = z.object({
    reset: PersonalReset,
});
export const ListPersonalResetsResponse = z.object({
    resets: z.array(PersonalReset),
});
// Licensed audio library for resets, served from the Thrive GraphQL gateway
// (UMG catalog etc.) through core-api. `available: false` means the gateway
// is unconfigured or unreachable; clients show an honest unavailable state.
export const ResetAudioLibraryTrack = z.object({
    id: z.string().min(1),
    title: z.string().default(""),
    artist: z.string().default(""),
    url: z.string().min(1),
    durationSec: z.number().positive(),
    thumbnailUrl: z.string().nullable().default(null),
});
export const ListResetAudioLibraryQuery = z.object({
    // Comma-separated genre list, e.g. "Rock" or "Rock,Pop".
    genres: z.string().trim().min(1).max(200).optional(),
    // 1-based catalog page. The gateway paginates with page + limit; the
    // page size is fixed server-side.
    page: z.coerce.number().int().min(1).max(50).optional(),
});
export const ListResetAudioLibraryResponse = z.object({
    tracks: z.array(ResetAudioLibraryTrack),
    available: z.boolean(),
    // True when another page likely exists. The gateway's pageInfo.hasNextPage
    // is unreliable, so this is derived from a full page being returned; the
    // final fetch may come back empty and simply ends the scroll.
    hasMore: z.boolean().default(false),
});
export const GetPersonalResetResponse = z.object({
    reset: PersonalReset,
});
export const DeletePersonalResetResponse = z.object({
    ok: z.literal(true),
});
// Sharing: the author picks one of two modes.
//   - "direct": notify up to 10 teammates; recipients see the reset in
//     "Shared with me" and get a push.
//   - "wall": publish to the company community feed (sets
//     publishedToCommunity = true). Visible to the whole company.
export const SharePersonalResetRequest = z
    .object({
    mode: z.enum(["direct", "wall"]).default("direct"),
    recipientUserIds: z.array(z.string().uuid()).max(10).optional(),
    note: z.string().trim().max(1000).optional(),
})
    .refine((data) => data.mode === "wall" || (data.recipientUserIds && data.recipientUserIds.length > 0), {
    message: "recipientUserIds is required when mode is direct",
});
export const SharePersonalResetResponse = z.object({
    shared: z.boolean(),
    publishedToCommunity: z.boolean(),
});
// A direct share row surfaced to a recipient: author + reset summary. The
// frames/audio are NOT included here; the recipient opens the player to load
// the full reset by id.
export const SharedPersonalResetSummary = z.object({
    id: z.string().uuid(),
    resetId: z.string().uuid(),
    fromUserId: z.string().uuid(),
    fromDisplayName: z.string(),
    note: z.string().nullable(),
    title: z.string(),
    posterImageUrl: z.string().nullable(),
    durationSec: z.number().positive().default(PERSONAL_RESET_DURATION_SEC),
    sharedAt: z.string(),
});
export const ListSharedPersonalResetsResponse = z.object({
    shares: z.array(SharedPersonalResetSummary),
});
// Author + summary used for the company-wide community list. firstFrame
// (image + filter + overlays) lets cards render a faithful composited
// poster without shipping the whole montage.
export const PersonalResetFeedItem = z.object({
    reset: PersonalReset.omit({ frames: true, audio: true, audioTracks: true }),
    firstFrame: PersonalResetFrame.nullable().default(null),
    reactions: z.array(CommunityReactionSummary).default([]),
});
export const ListCommunityPersonalResetsResponse = z.object({
    resets: z.array(PersonalResetFeedItem),
});
// Used by the asset route to attach an uploaded image to a personal reset row
// for poster/thumbnail usage. Reuses the CommunityAsset shape so the existing
// upload route and storage pipeline handle safety scanning.
export { CommunityAsset };
//# sourceMappingURL=personal-reset.js.map